import * as React from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  Avatar,
  Boton,
  Campo,
  Card,
  CardContenido,
  CardDescripcion,
  CardEncabezado,
  CardTitulo,
  Input,
  Pill,
  Select,
  SelectContenido,
  SelectDisparador,
  SelectOpcion,
  SelectValor,
  useToast,
} from "@iavmx/ui";
import { MarcoAcceso, Mensaje } from "../componentes";
import { leer } from "../api";
import { useSesion } from "../sesion";
import { primerNombre } from "../fechas";
import type { Area, Yo } from "../tipos";
import { nombreRolApp, type ClaveApp } from "../../compartido/apps";

const NINGUNO = "__ninguno__";

/** Primera entrada: nombre, área y foto; y lo que va a tener. */
type Catalogo = { areas: Area[]; personas: { id: string; nombre: string; rol: string | null }[] };

/** Primera entrada: nombre, área y foto; y lo que va a tener. Espera a la persona y al catálogo antes de armar el formulario. */
export function Bienvenida() {
  const { yo, estadoYo, cargando, correo, api } = useSesion();
  const navegar = useNavigate();

  React.useEffect(() => {
    if (!cargando && !correo) void navegar({ to: "/entrar", replace: true });
  }, [cargando, correo, navegar]);

  const catalogo = useQuery({
    queryKey: ["areas"],
    enabled: estadoYo === "ok",
    queryFn: () => api.api.areas.$get().then((r) => leer<Catalogo>(r)),
  });

  // El formulario se monta con el catálogo ya cargado: así los selectores nacen con su valor y sus opciones a la vez.
  if (cargando || estadoYo === "cargando" || !yo || !catalogo.data)
    return (
      <MarcoAcceso>
        <p className="font-ui text-sm text-tinta-tenue" aria-busy="true">Preparando tu primera entrada…</p>
      </MarcoAcceso>
    );
  return <Formulario yo={yo} catalogo={catalogo.data} />;
}

function Formulario({ yo, catalogo }: { yo: Yo; catalogo: Catalogo }) {
  const { api, token, recargarYo } = useSesion();
  const navegar = useNavigate();
  const { avisar } = useToast();
  const [nombre, setNombre] = React.useState(yo.nombre);
  const [areaId, setAreaId] = React.useState<string>(yo.areaId ?? NINGUNO);
  const [jefeId, setJefeId] = React.useState<string>(yo.jefeId ?? NINGUNO);
  const [encargos, setEncargos] = React.useState(yo.comoRecibirEncargos ?? "");
  const [fotoUrl, setFotoUrl] = React.useState<string | null>(yo.fotoUrl);
  const [error, setError] = React.useState<string | null>(null);
  const [guardado, setGuardado] = React.useState(false);

  // Al portal solo cuando la persona ya llegó actualizada: si se navega antes, la raíz ve
  // el perfil incompleto y regresa aquí (TanStack avisa a los componentes un tick después).
  const { yo: yoActual } = useSesion();
  React.useEffect(() => {
    if (guardado && yoActual?.perfilCompleto) void navegar({ to: "/", replace: true });
  }, [guardado, yoActual, navegar]);

  const guardar = useMutation({
    mutationFn: async () => {
      if (nombre.trim().length < 2) throw new Error("Escribe tu nombre como quieres que te vean.");
      if (areaId === NINGUNO) throw new Error("Elige tu área.");
      return api.api.perfil
        .$put({
          json: {
            nombre: nombre.trim(),
            areaId,
            jefeId: jefeId === NINGUNO ? null : jefeId,
            comoRecibirEncargos: encargos.trim() || null,
          },
        })
        .then((r) => leer<{ ok: true }>(r));
    },
    onSuccess: async () => {
      setGuardado(true);
      avisar({ titulo: "Listo, ya estás dentro", tono: "exito" });
      await recargarYo();
    },
    onError: (e) => setError(e.message),
  });

  const subirFoto = useMutation({
    mutationFn: async (archivo: File) => {
      const form = new FormData();
      form.append("foto", archivo);
      const t = await token();
      const r = await fetch("/api/perfil/foto", {
        method: "POST",
        body: form,
        headers: t ? { authorization: `Bearer ${t}` } : {},
      });
      return leer<{ fotoUrl: string | null }>(r);
    },
    onSuccess: (r) => {
      setFotoUrl(r.fotoUrl);
      avisar({ titulo: "Foto guardada", tono: "exito" });
    },
    onError: (e) => avisar({ titulo: "No se pudo guardar la foto", descripcion: e.message, tono: "error" }),
  });

  const mias = yo.herramientas.filter((h) => h.tiene);
  const pronto = yo.herramientas.filter((h) => !h.disponible);

  return (
    <MarcoAcceso ancho="max-w-5xl" derecha={<span className="hidden sm:inline">{yo.correo}</span>}>
      <div className="space-y-8">
        <div>
          <h1 className="font-titulo text-3xl font-bold leading-tight sm:text-4xl">
            Bienvenido, {primerNombre(nombre || yo.nombre)}.
          </h1>
          <p className="mt-2 max-w-2xl font-ui text-sm text-tinta-tenue">
            {yo.invitadoPorNombre
              ? `${yo.invitadoPorNombre} te invitó. Revisa tu nombre, confirma tu área y, si quieres, sube una foto; con eso ya puedes entrar.`
              : "Revisa tu nombre, confirma tu área y, si quieres, sube una foto; con eso ya puedes entrar."}
          </p>
        </div>

        <ol className="grid max-w-2xl grid-cols-3 gap-3 font-ui text-xs" aria-label="Pasos">
          <li className="min-w-0"><div className="mb-2 h-1 bg-tinta" /><span className="font-semibold">Cuenta verificada</span><span className="block truncate text-tinta-tenue" title={yo.correo}>{yo.correo}</span></li>
          <li className="min-w-0" aria-current="step"><div className="mb-2 h-1 bg-azul" /><span className="font-semibold">Tus datos</span><span className="block truncate text-tinta-tenue">Nombre, área y foto</span></li>
          <li className="min-w-0"><div className="mb-2 h-1 bg-fondo-alzado" /><span className="font-semibold text-tinta-tenue">Al portal</span><span className="block truncate text-tinta-tenue">Tus herramientas listas</span></li>
        </ol>

        <div className="grid gap-10 lg:grid-cols-[minmax(0,520px)_1fr]">
          <form
            className="space-y-5"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              guardar.mutate();
            }}
            noValidate
          >
            <h2 className="font-titulo text-xl font-bold">Confirma tus datos</h2>
            <div className="flex items-center gap-4">
              <Avatar nombre={nombre || yo.nombre} src={fotoUrl} className="size-16" />
              <div className="space-y-1">
                <p className="font-ui text-sm font-semibold">Tu foto</p>
                <p className="font-ui text-xs text-tinta-tenue">Se usa en el directorio y en los avisos para que te reconozcan. JPG, PNG o WebP hasta 2 MB.</p>
                <label className="inline-flex min-h-9 cursor-pointer items-center border border-linea-fuerte px-3 font-ui text-xs font-semibold hover:bg-fondo-suave focus-within:ring-2 focus-within:ring-ring">
                  {subirFoto.isPending ? "Subiendo…" : fotoUrl ? "Cambiar foto" : "Subir una foto"}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="sr-only"
                    disabled={subirFoto.isPending}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) subirFoto.mutate(f);
                      e.target.value = "";
                    }}
                  />
                </label>
              </div>
            </div>
            <Campo etiqueta="Nombre como quieres que te vean" htmlFor="nombre">
              <Input id="nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} className="h-11 text-base" autoComplete="name" required />
            </Campo>
            <Campo etiqueta="Correo" htmlFor="correo" ayuda="Viene de tu cuenta; no se cambia aquí.">
              <Input id="correo" value={yo.correo} readOnly className="h-11 bg-fondo-suave text-base text-tinta-3" />
            </Campo>
            <Campo
              etiqueta="Área"
              htmlFor="area"
              ayuda={yo.invitadoPorNombre && yo.areaId ? `La propuso ${yo.invitadoPorNombre} al invitarte. Si no es la tuya, cámbiala; recibe el aviso.` : undefined}
            >
              <Select value={areaId} onValueChange={setAreaId}>
                <SelectDisparador id="area" className="h-11 text-base" aria-label="Área">
                  <SelectValor placeholder="Elige tu área" />
                </SelectDisparador>
                <SelectContenido>
                  <SelectOpcion value={NINGUNO}>Elige tu área</SelectOpcion>
                  {catalogo.areas.map((a) => (
                    <SelectOpcion key={a.id} value={a.id}>{a.nombre}</SelectOpcion>
                  ))}
                </SelectContenido>
              </Select>
            </Campo>
            <Campo etiqueta="Tu jefe directo" htmlFor="jefe">
              <Select value={jefeId} onValueChange={setJefeId}>
                <SelectDisparador id="jefe" className="h-11 text-base" aria-label="Jefe directo">
                  <SelectValor placeholder="Elige a tu jefe" />
                </SelectDisparador>
                <SelectContenido>
                  <SelectOpcion value={NINGUNO}>Sin jefe directo</SelectOpcion>
                  {catalogo.personas.filter((p) => p.id !== yo.id).map((p) => (
                    <SelectOpcion key={p.id} value={p.id}>{p.nombre}</SelectOpcion>
                  ))}
                </SelectContenido>
              </Select>
            </Campo>
            <Campo etiqueta="Cómo prefieres recibir encargos" htmlFor="encargos" ayuda="Aparece en tu ficha del directorio. Ejemplo: «WhatsApp solo para urgencias».">
              <Input id="encargos" value={encargos} onChange={(e) => setEncargos(e.target.value)} className="h-11 text-base" maxLength={240} placeholder="Correo con el documento adjunto" />
            </Campo>
            {error && <Mensaje tono="error">{error}</Mensaje>}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-linea pt-5">
              <span className="font-ui text-xs text-tinta-tenue">Todo esto se puede cambiar después en Ajustes.</span>
              <Boton type="submit" variante="acento" className="h-11 px-6 text-tinta" disabled={guardar.isPending}>
                {guardar.isPending ? "Guardando…" : "Entrar al portal"}
              </Boton>
            </div>
          </form>

          <section className="space-y-4">
            <div>
              <h2 className="font-titulo text-xl font-bold">Lo que vas a tener</h2>
              <p className="mt-1 font-ui text-sm text-tinta-tenue">Según tu área y lo que decidió quien te invitó. Si te falta algo, se pide desde el portal.</p>
            </div>
            {mias.map((h) => (
              <Card key={h.clave}>
                <CardEncabezado>
                  <div className="flex items-start justify-between gap-2">
                    <CardTitulo>{h.nombre}</CardTitulo>
                    {h.rol && <Pill>{nombreRolApp(h.app as ClaveApp, h.rol)}</Pill>}
                  </div>
                  <CardDescripcion>{h.frase}</CardDescripcion>
                </CardEncabezado>
                <CardContenido className="font-ui text-xs text-tinta-tenue">{h.url.replace(/^https?:\/\//, "")}</CardContenido>
              </Card>
            ))}
            {mias.length === 0 && <Mensaje tono="info">Todavía no tienes herramientas asignadas. Quien te invitó puede dártelas desde Administración.</Mensaje>}
            {pronto.length > 0 && (
              <p className="font-ui text-xs text-tinta-tenue">
                Próximamente: {pronto.map((h) => h.nombre).join(", ")}.
              </p>
            )}
          </section>
        </div>
      </div>
    </MarcoAcceso>
  );
}
